/**
 * The audit's colour language, kept in one place so the table, the legend and
 * the summary can never drift apart.
 *
 * Same four verdicts and the same hexes the command-line report used, so a
 * reviewer moving from the spreadsheet to this screen reads them identically:
 *
 *   MATCH     the two systems say the same thing
 *   PARTIAL   same substance, different wording or convention - including
 *             differences we know are structural, like a start date that
 *             resets on every renewal
 *   MISMATCH  both hold a real value and the values genuinely disagree
 *   ABSENT    our schema has no such field, or it is empty here
 */
/**
 * Brand accent, stated literally rather than taken from `.bg-primary` /
 * `.text-primary` / `.border-primary`.
 *
 * Those utilities DO NOT WORK IN LIGHT MODE in this theme. They resolve to
 * `rgba(var(--pelmeds-blue-rgb), …)`, and `--pelmeds-blue-rgb` is defined as
 * the hex `#154D86` under `[data-bs-theme=light]` (it is only a valid
 * "r, g, b" triplet under `[data-bs-theme=dark]`). `rgba(#154D86, 1)` is
 * invalid CSS, so the browser discards the whole declaration and the element
 * paints nothing at all - which is why anything relying on them came out blank.
 *
 * `.btn-primary` is unaffected: it uses `var(--pelmeds-blue)` directly, not
 * inside an rgba(), and that resolves to #154D86. So BRAND below is the colour
 * the app's own buttons already use, and matching it keeps this page on-brand.
 */
export const BRAND = {
  base: '#154D86',
  soft: 'rgba(21,77,134,0.10)',
  softer: 'rgba(21,77,134,0.06)',
  ring: 'rgba(21,77,134,0.18)',
  track: 'rgba(21,77,134,0.16)'
};

export const STATUS_TONE = {
  MATCH: { color: '#1B7A46', bg: 'rgba(27,122,70,0.10)', label: 'Match' },
  PARTIAL: { color: '#B36806', bg: 'rgba(179,104,6,0.12)', label: 'Partial' },
  MISMATCH: { color: '#BE2A1E', bg: 'rgba(190,42,30,0.12)', label: 'No match' },
  ABSENT: { color: '#BE2A1E', bg: 'rgba(190,42,30,0.07)', label: 'Not held' }
};

export const toneFor = status => STATUS_TONE[status] || STATUS_TONE.ABSENT;

/** Row verdicts, as the summary tiles and the row badges label them. */
export const VERDICT = {
  EXACT_MATCH: { label: 'Agrees', variant: 'success' },
  SYSTEMIC_ONLY: { label: 'Known gap only', variant: 'warning' },
  FIELD_MISMATCH: { label: 'Needs review', variant: 'danger' }
};

export default { BRAND, STATUS_TONE, toneFor, VERDICT };
